import type { FieldError, Problem, ProblemCode } from '@/types/api'

/**
 * 后端错误的运行时载体。
 *
 * 后端所有错误都是 RFC 9457 `application/problem+json`，并明确要求
 * 「客户端应按 `code` 分支，不要按 `detail` 文本」。因此这个类把 `code`
 * 放在一等位置，`detail` 只作为兜底文案。
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: ProblemCode
  readonly problem: Problem
  readonly requestId: string | undefined
  readonly fieldErrors: FieldError[]
  readonly retryAfterSeconds: number | undefined

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.code = problem.code
    this.problem = problem
    this.requestId = problem.instance
    this.fieldErrors = problem.errors ?? []
    this.retryAfterSeconds = problem.retry_after_seconds
  }

  /** 字段级错误按 JSON Pointer 取用，供表单把错误挂到对应输入框。 */
  fieldError(pointer: string): FieldError | undefined {
    return this.fieldErrors.find((error) => error.pointer === pointer)
  }
}

/** 网络层面的失败（断网、超时、CORS），拿不到 Problem 时使用。 */
export class NetworkError extends Error {
  constructor(message = '网络请求失败，请检查连接后重试。') {
    super(message)
    this.name = 'NetworkError'
  }
}

/** 请求被主动取消（组件卸载、查询条件变化）。调用方通常应静默忽略。 */
export class AbortError extends Error {
  constructor() {
    super('请求已取消')
    this.name = 'AbortError'
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function isAbortError(error: unknown): boolean {
  return error instanceof AbortError || (error instanceof DOMException && error.name === 'AbortError')
}

/** 判断是否为某个具体的问题码，避免调用处到处写 `instanceof` + 属性访问。 */
export function hasProblemCode(error: unknown, ...codes: ProblemCode[]): boolean {
  return isApiError(error) && codes.includes(error.code)
}

const FALLBACK_MESSAGES: Partial<Record<ProblemCode, string>> = {
  authentication_required: '请先登录后再继续。',
  forbidden: '没有权限执行该操作。',
  /*
    与 `forbidden` 分开的意义在于「下一步做什么」：这个码要求重置人机验证控件
    并取一个新 token。三种成因的具体措辞在 api/turnstile.ts 的 `challengeHint`，
    调用点应当优先用它；这里只是没有接上下文时的兜底。
  */
  challenge_failed: '人机验证未通过，请重新验证后再试。',
  not_found: '内容不存在或已下架。',
  invalid_cursor: '翻页信息已过期，请回到第一页。',
  gone: '下载会话已过期，请重新获取下载链接。',
  rate_limited: '操作过于频繁，请稍后再试。',
  validation_failed: '提交的内容不符合要求，请检查后重试。',
  unsupported_parameter: '当前版本不支持该查询条件。',
  state_conflict: '当前状态下无法执行该操作。',
  title_conflict: '已存在同名内容，请换一个标题。',
  internal_error: '服务端出现异常，请稍后重试。',

  /*
    以下是管理端才会遇到的码。它们的后端 detail 都很准确但很技术
    （「If-Match 与当前版本不符」），对着操作者要说的是**下一步该做什么**。
  */
  invalid_request: '请求格式不正确，请刷新页面后重试。',
  precondition_failed: '这条记录已被其他人修改，请刷新后基于最新内容重试。',
  precondition_required: '缺少版本号，无法安全提交。请刷新页面后重试。',
  reference_in_use: '仍有已发布内容在引用它，不能删除。',
  media_in_use: '这张图片正被已发布内容引用，不能删除。',
  replacement_exists: '该序号已经有替代卷，不能重复创建。',
  purge_already_started: '物理清理已经开始，无法再恢复。',
  idempotency_in_progress: '同一操作正在处理中，请稍候再试。',
  idempotency_key_reused: '幂等键已用于另一份请求内容，请重新发起操作。',
  upload_manifest_conflict: '提交的分片清单与此前不一致，请重新读取续传状态。',
}

/**
 * 把任意异常翻译成一句可以直接放进 UI 的中文。
 *
 * 优先级：问题码的固定文案 > 后端 `detail` > 后端 `title`。之所以让固定文案
 * 优先，是因为后端 `detail` 面向开发者，且刻意不含 URL / 键名，读起来往往
 * 比不上一句针对场景写好的话。
 */
export function toMessage(error: unknown, fallback = '出现了一点问题，请稍后重试。'): string {
  if (isAbortError(error)) return ''
  if (error instanceof NetworkError) return error.message
  if (isApiError(error)) {
    return FALLBACK_MESSAGES[error.code] ?? error.problem.detail ?? error.problem.title ?? fallback
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}
